import { IModel, Model } from "@common/models/Model";
import { StringUtility } from "@common/utils/stringUtility";
import { PhotoModel, StorageContentModel } from ".";
import { LabOrderEventListingModel } from "./LabOrderEventListingModel";
import { LabOrderTestListingModel } from "./LabOrderTestListingModel";

export interface ILabOrderModel extends IModel {
    labOrderId: number;
    labOrderPublicId: string;
    visitId: number;
    clientId: number;
    petId: number;
    clinicId: number;
    storeId: number;
    sourceSystemId: number;
    veterinarianId?: number;
    licenseId?: number;
    diagnosticResultsDocumentId?: number;
    dateResulted?: string;
    // Client Fields
    clientPublicId: string;
    clientFirstName: string;
    clientLastName: string;
    clientEmailAddress: string;
    clientPhone: string;
    clientAddress: string;
    clientAddress2: string;
    clientCity: string;
    clientStateAbbreviation: string;
    clientZipCode: string;
    clientCounty: string;

    //Pet Fields
    petPublicId: string;
    petName: string;
    petSpeciesName: string;
    petGenderName: string;
    petBreedName: string;
    petBirthDate: string;
    petWeight: number;
    petMedications: string;
    petMicrochipNumber: string;
    petRabiesTagNumber: string;

    // Vet Fields
    veterinarianPublicId: string;
    vetFirstName: string;
    vetLastName: string;
    vetEmail: string;
    vetLicenseNumber: string;
    vetLicenseStateAbbreviation: string;
    vetLicenseExpirationDate: string;
    vetLicenseLicenseTypeName: string;
    vetSignaturePhotoId?: number;

    // Technician Fields
    technicianFirstName: string;
    technicianLastName: string;

    // Clinic Fields
    storeCode: string;
    storeName: string;
    storeAddress: string;
    storeAddress2: string;
    storeCity: string;
    storeStateAbbreviation: string;
    storeZipCode: string;
    storePhone: string;
    storeSecondaryPhone: string;
    storeBrandName: string;
    storePartnerName: string;
    storePartnerLabel: string;
    storeTimezoneName: string;
    storeLatitude: number;
    storeLongitude: number;

    // Other Fields
    isInHouseDiagnostic: boolean;
    specimenCollectionDate: string;
    inHouseLabStationDevices: string;
    paperlessBarcode: string; // i.e. IDEXX's PLOBarcode

    // Lab Supplied Order Fields
    orderSourceSystemKey: string;
    orderStatus: string;
    orderUIUrl: string;
    orderPDFUrl: string;

    // Lab Supplied Result Fields
    resultSourceSystemKey: string; // i.e. The Lab's ResultId
    resultDataPayload: string; // i.e. The Lab's Result Data in JSON format.  This will change over time as partial and complete results flow in
    resultUIUrl: string; // i.e. The Lab's Result UI URL.  This is the URL we use if we want to show Refernce Lab's Results UI.  The result of this URL will be JSON, with a time-sensitive, single-use, URL that is used in a browser.
    resultPDFUrl: string; // i.e. The Lab's PDF URL. This is the url we use to *obtain* the PDF. The resulting PDF is then stored in PetPass for repeatid reads from our infrastructure. See DiagnosticResultsDocumentId and DiagnosticResultsDocument fields.
    resultStatus: string | undefined; // i.e. The Lab's Status. This will change over time as partial and complete results are received.
    resultModality: string; // i.e. The Lab's Modality. Ref-Lab vs In-House

    vetSignaturePhoto: PhotoModel;
    diagnosticResultsDocument: StorageContentModel;
    orderPDFDocument: StorageContentModel;
    events: LabOrderEventListingModel[];
    tests: LabOrderTestListingModel[];

    notes: string;
    reviewedBy: string;
    communicatedBy: string;
}

export class LabOrderModel extends Model implements ILabOrderModel {
    labOrderId: number;
    labOrderPublicId: string;
    visitId: number;
    clientId: number;
    petId: number;
    clinicId: number;
    storeId: number;
    sourceSystemId: number;
    veterinarianId?: number;
    licenseId?: number;
    diagnosticResultsDocumentId?: number;

    // Client Fields
    clientPublicId: string;
    clientFirstName: string;
    clientLastName: string;
    clientEmailAddress: string;
    clientPhone: string;
    clientAddress: string;
    clientAddress2: string;
    clientCity: string;
    clientStateAbbreviation: string;
    clientZipCode: string;
    clientCounty: string;
    dateResulted?: string;
    //Pet Fields
    petPublicId: string;
    petName: string;
    petSpeciesName: string;
    petGenderName: string;
    petBreedName: string;
    petBirthDate: string;
    petWeight: number;
    petMedications: string;
    petMicrochipNumber: string;
    petRabiesTagNumber: string;

    // Vet Fields
    veterinarianPublicId: string;
    vetFirstName: string;
    vetLastName: string;
    vetEmail: string;
    vetLicenseNumber: string;
    vetLicenseStateAbbreviation: string;
    vetLicenseExpirationDate: string;
    vetLicenseLicenseTypeName: string;
    vetSignaturePhotoId?: number;

    // Technician Fields
    technicianFirstName: string;
    technicianLastName: string;

    // Clinic Fields
    storeCode: string;
    storeName: string;
    storeAddress: string;
    storeAddress2: string;
    storeCity: string;
    storeStateAbbreviation: string;
    storeZipCode: string;
    storePhone: string;
    storeSecondaryPhone: string;
    storeBrandName: string;
    storePartnerName: string;
    storePartnerLabel: string;
    storeTimezoneName: string;
    storeLatitude: number;
    storeLongitude: number;

    // Other Fields
    isInHouseDiagnostic: boolean;
    specimenCollectionDate: string;
    inHouseLabStationDevices: string;
    paperlessBarcode: string; // i.e. IDEXX's PLOBarcode

    // Lab Supplied Order Fields
    orderSourceSystemKey: string;
    orderStatus: string;
    orderUIUrl: string;
    orderPDFUrl: string;

    // Lab Supplied Result Fields
    resultSourceSystemKey: string; // i.e. The Lab's ResultId
    resultDataPayload: string; // i.e. The Lab's Result Data in JSON format.  This will change over time as partial and complete results flow in
    resultUIUrl: string; // i.e. The Lab's Result UI URL.  This is the URL we use if we want to show Refernce Lab's Results UI.  The result of this URL will be JSON, with a time-sensitive, single-use, URL that is used in a browser.
    resultPDFUrl: string; // i.e. The Lab's PDF URL. This is the url we use to *obtain* the PDF. The resulting PDF is then stored in PetPass for repeatid reads from our infrastructure. See DiagnosticResultsDocumentId and DiagnosticResultsDocument fields.
    resultStatus: string | undefined; // i.e. The Lab's Status. This will change over time as partial and complete results are received.
    resultModality: string; // i.e. The Lab's Modality. Ref-Lab vs In-House

    vetSignaturePhoto: PhotoModel;
    diagnosticResultsDocument: StorageContentModel;
    orderPDFDocument: StorageContentModel;
    events: LabOrderEventListingModel[];
    tests: LabOrderTestListingModel[];

    clientName: string;
    vetName: string;

    notes: string;
    reviewedBy: string;
    communicatedBy: string;

    constructor(params: Partial<ILabOrderModel> = {}) {
        super(params);
        const {
            labOrderId = 0,
            labOrderPublicId = '',
            visitId = 0,
            clientId = 0,
            petId = 0,
            clinicId = 0,
            storeId = 0,
            sourceSystemId = 0,
            veterinarianId = 0,
            licenseId = 0,
            diagnosticResultsDocumentId = 0,
            clientPublicId = '',
            clientFirstName = '',
            clientLastName = '',
            clientEmailAddress = '',
            clientPhone = '',
            clientAddress = '',
            clientAddress2 = '',
            clientCity = '',
            clientStateAbbreviation = '',
            clientZipCode = '',
            clientCounty = '',
            petPublicId = '',
            petName = '',
            petSpeciesName = '',
            petGenderName = '',
            petBreedName = '',
            petBirthDate = '',
            petWeight = 0,
            petMedications = '',
            petMicrochipNumber = '',
            petRabiesTagNumber = '',
            veterinarianPublicId = '',
            vetFirstName = '',
            vetLastName = '',
            vetEmail = '',
            vetLicenseNumber = '',
            vetLicenseStateAbbreviation = '',
            vetLicenseExpirationDate = '',
            vetLicenseLicenseTypeName = '',
            vetSignaturePhotoId = 0,
            technicianFirstName = '',
            technicianLastName = '',
            storeCode = '',
            storeName = '',
            storeAddress = '',
            storeAddress2 = '',
            storeCity = '',
            storeStateAbbreviation = '',
            storeZipCode = '',
            storePhone = '',
            storeSecondaryPhone = '',
            storeBrandName = '',
            storePartnerName = '',
            storePartnerLabel = '',
            storeTimezoneName = '',
            storeLatitude = 0,
            storeLongitude = 0,
            isInHouseDiagnostic = false,
            specimenCollectionDate = '',
            inHouseLabStationDevices = '',
            paperlessBarcode = '',
            orderSourceSystemKey = '',
            orderStatus = '',
            orderUIUrl = '',
            orderPDFUrl = '',
            resultSourceSystemKey = '',
            resultDataPayload = '',
            resultUIUrl = '',
            resultPDFUrl = '',
            resultStatus = '',
            resultModality = '',
            vetSignaturePhoto = new PhotoModel(),
            diagnosticResultsDocument = new StorageContentModel(),
            orderPDFDocument = new StorageContentModel(),
            events = [],
            tests = [],
            notes = '',
            reviewedBy = '',
            communicatedBy = '',
            dateResulted = ''
        } = params;

        this.labOrderId = labOrderId;
        this.labOrderPublicId = labOrderPublicId;
        this.visitId = visitId;
        this.clientId = clientId;
        this.petId = petId;
        this.clinicId = clinicId;
        this.storeId = storeId;
        this.sourceSystemId = sourceSystemId;
        this.veterinarianId = veterinarianId;
        this.licenseId = licenseId;
        this.diagnosticResultsDocumentId = diagnosticResultsDocumentId;
        this.clientPublicId = clientPublicId;
        this.clientFirstName = clientFirstName;
        this.clientLastName = clientLastName;
        this.clientEmailAddress = clientEmailAddress;
        this.clientPhone = clientPhone;
        this.clientAddress = clientAddress;
        this.clientAddress2 = clientAddress2;
        this.clientCity = clientCity;
        this.clientStateAbbreviation = clientStateAbbreviation;
        this.clientZipCode = clientZipCode;
        this.clientCounty = clientCounty;
        this.petPublicId = petPublicId;
        this.petName = petName;
        this.petSpeciesName = petSpeciesName;
        this.petGenderName = petGenderName;
        this.petBreedName = petBreedName;
        this.petBirthDate = petBirthDate;
        this.petWeight = petWeight;
        this.petMedications = petMedications;
        this.petMicrochipNumber = petMicrochipNumber;
        this.petRabiesTagNumber = petRabiesTagNumber;
        this.veterinarianPublicId = veterinarianPublicId;
        this.vetFirstName = vetFirstName;
        this.vetLastName = vetLastName;
        this.vetEmail = vetEmail;
        this.vetLicenseNumber = vetLicenseNumber;
        this.vetLicenseStateAbbreviation = vetLicenseStateAbbreviation;
        this.vetLicenseExpirationDate = vetLicenseExpirationDate;
        this.vetLicenseLicenseTypeName = vetLicenseLicenseTypeName;
        this.vetSignaturePhotoId = vetSignaturePhotoId;
        this.technicianFirstName = technicianFirstName;
        this.technicianLastName = technicianLastName;
        this.storeCode = storeCode;
        this.storeName = storeName;
        this.storeAddress = storeAddress;
        this.storeAddress2 = storeAddress2;
        this.storeCity = storeCity;
        this.storeStateAbbreviation = storeStateAbbreviation;
        this.storeZipCode = storeZipCode;
        this.storePhone = storePhone;
        this.storeSecondaryPhone = storeSecondaryPhone;
        this.storeBrandName = storeBrandName;
        this.storePartnerName = storePartnerName;
        this.storePartnerLabel = storePartnerLabel;
        this.storeTimezoneName = storeTimezoneName;
        this.storeLatitude = storeLatitude;
        this.storeLongitude = storeLongitude;
        this.isInHouseDiagnostic = isInHouseDiagnostic;
        this.specimenCollectionDate = specimenCollectionDate;
        this.inHouseLabStationDevices = inHouseLabStationDevices;
        this.paperlessBarcode = paperlessBarcode;
        this.orderSourceSystemKey = orderSourceSystemKey;
        this.orderStatus = orderStatus;
        this.orderUIUrl = orderUIUrl;
        this.orderPDFUrl = orderPDFUrl;
        this.resultSourceSystemKey = resultSourceSystemKey;
        this.resultDataPayload = resultDataPayload;
        this.resultUIUrl = resultUIUrl;
        this.resultPDFUrl = resultPDFUrl;
        this.resultStatus = resultStatus;
        this.resultModality = resultModality;
        this.vetSignaturePhoto = vetSignaturePhoto;
        this.diagnosticResultsDocument = diagnosticResultsDocument;
        this.orderPDFDocument = orderPDFDocument;
        this.events = events;
        this.tests = tests;

        this.clientName = StringUtility.formattedUserName(clientFirstName, clientLastName, undefined);
        this.vetName = StringUtility.formattedUserName(vetFirstName, vetLastName, "Dr");

        this.notes = notes;
        this.communicatedBy = communicatedBy;
        this.reviewedBy = reviewedBy;
        this.dateResulted = dateResulted;
    }
}
